import { useEffect, useState } from 'react';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { useReferrals } from '@Hooks/useReferrals';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { getMemberProfile } from '@Utils/fish';
import { useNavigate } from 'react-router-dom';

// ---------- column configs ----------

const referralColumnConfigs: ColumnConfig<Referred>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name,
    type: 'text',
    size: 120,
  },
  {
    id: 'phoneNumber',
    headerLabel: 'Phone',
    accessor: (row) => row.phoneNumber,
    type: 'text',
    size: 120,
  },
  {
    id: 'subscription',
    headerLabel: 'Subscription',
    accessor: (row) => row.subscription ?? '',
    type: 'text',
  },
  {
    id: 'registrationCode',
    headerLabel: 'Registration Code',
    accessor: (row) => row.registrationCode ?? 'N/A',
    type: 'text',
  },
];

const REFERRAL_FIELD_MAPPING: Record<string, string> = {
  name: 'filter[name]',
  description: 'filter[description]',
};

const REFERRAL_STATIC_PARAMS: Record<string, string> = {
  '[filter][deletedAt]': 'null',
  'include[subscriptions][filter][deletedAt]': 'null',
  'include[subscriptions][include][fish][filter][deletedAt]': 'null',
  'include[subscriptions][include][fish][include][fishProfiles][filter][deletedAt]':
    'null',
};

type Referred = {
  fishId: string;
  name: string;
  phoneNumber: string;
  subscription: string;
  registrationCode: string;
};

const ReferralList = ({ memberId }) => {
  const [referredList, setReferredList] = useState([]);
  const navigate = useNavigate();

  const controller = useResourceTableController<Referred>({
    columnsConfig: referralColumnConfigs,
    defaultSort: {
      id: 'id',
      desc: false,
    },
    baseSearchParams: () => ({
      'filter[referrerId]': memberId,
      'include': 'subscriptions.fish.fishProfiles,registrationCode',
    }),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: REFERRAL_FIELD_MAPPING,
        staticParams: REFERRAL_STATIC_PARAMS,
      }),
    quickFilterFields: [{ field: 'name', label: 'Name' }],
    initialPageSize: 10,
    initialPinning: {
      left: ['select', 'name'],
      right: [],
    },
  });

  const { data: referralPayload, status } = useReferrals({
    searchParams: controller.searchParams,
  });

  useEffect(() => {
    const referredFish = [];
    if (referralPayload?.data && Array.isArray(referralPayload.data)) {
      for (const referral of referralPayload.data) {
        for (const sub of referral.subscriptions ?? []) {
          if (!sub.fish) continue;

          const fish = sub.fish;
          const fishId = fish.id;
          const profile = getMemberProfile(fish) ?? fish.fishProfiles[0];
          const name = `${profile?.fName || 'N/A'} ${profile?.lName || ''}`;
          const phoneNumber = fish?.phoneNumber ?? 'N/A';
          const subscription = sub?.subscriptionType;
          const registrationCode = referral?.registrationCode?.code ?? 'N/A';

          referredFish.push({
            fishId,
            name,
            phoneNumber,
            subscription,
            registrationCode,
          });
        }
      }
    }
    setReferredList(referredFish);
  }, [referralPayload]);

  const rows: Referred[] = referredList;

  const meta = Array.isArray(referredList) ? undefined : referralPayload?.meta;

  return (
    <ResourceTableView<Referred>
      label="Referred"
      controller={controller}
      data={rows}
      meta={meta}
      status={status as any}
      onRowClick={(row) => navigate(`/members/${row.fishId}`)}
    />
  );
};

export default ReferralList;
